import React from 'react';

import { Box, Flex, Inline, Stack } from '@atlaskit/primitives';
import { cssMap } from '@atlaskit/css';

import type { AIEventsInstrumentationConfig } from '@atlassian/ai-analytics';
import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics';

import { fg } from '@confluence/feature-gating';

import { useAudioBriefing } from '../../../../hooks/useAudioBriefing';
import { PlayPauseButton } from '../../../controls/PlayPauseButton';
import { PlayFromBeginningButton } from '../../../controls/PlayFromBeginningButton';
import { SkipBackButton } from '../../../controls/SkipBackButton';
import { SkipForwardButton } from '../../../controls/SkipForwardButton';
import { PlaybackSpeedControl } from '../../../controls/PlaybackSpeedControl';
import { AudioTimeIndicator } from '../../../controls/AudioTimeIndicator';
const aiAnalyticsConfig: AIEventsInstrumentationConfig = {
	product: 'confluence',
	subproduct: 'confluence',
	source: 'page',
	aiFeatureName: 'aiAudioBriefing',
	proactiveGeneratedAI: 0,
	userGeneratedAI: 1,
};

type AudioBriefingPopupListenPanelProps = {
	audioRef: React.RefObject<HTMLAudioElement>;
};

const styles = cssMap({
	wrapper: {
		flex: 1,
	},
	outerControlButtonWrapper: {
		// This is the width of the speed option that requires the most space
		// Ensures that the displayed speed is never truncated and that spacing remains uniform
		width: '54px',
	},
});

export const AudioBriefingPopupListenPanel = ({ audioRef }: AudioBriefingPopupListenPanelProps) => {
	const [{ isPlaying, isLoading, hasFetched }, { fetchBriefing }] = useAudioBriefing();

	return (
		<AIEventsInstrumentationProvider config={aiAnalyticsConfig}>
			<Box paddingBlock="space.100" xcss={styles.wrapper} testId="listen-panel-controls">
				<Stack space="space.150" alignInline="center">
					<Inline space="space.150" alignBlock="center">
						{fg('confluence_briefings_playback_speed') && (
							<Flex xcss={styles.outerControlButtonWrapper} justifyContent="center">
								<PlayFromBeginningButton audioRef={audioRef} isDisabled={isLoading} />
							</Flex>
						)}

						<SkipBackButton audioRef={audioRef} isDisabled={isLoading} />
						<PlayPauseButton
							audioRef={audioRef}
							isPlaying={isPlaying}
							isLoading={isLoading}
							hasFetched={hasFetched}
							fetchContent={fetchBriefing}
						/>
						<SkipForwardButton audioRef={audioRef} isDisabled={isLoading} />

						{fg('confluence_briefings_playback_speed') && (
							<Box xcss={styles.outerControlButtonWrapper}>
								<PlaybackSpeedControl />
							</Box>
						)}
					</Inline>
					<AudioTimeIndicator audioRef={audioRef} isLoading={isLoading} />
				</Stack>
			</Box>
		</AIEventsInstrumentationProvider>
	);
};
